import { useQuery } from '@apollo/client/react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SegmentedControl } from '~/components/SegmentedControl'
import { GQL_SCORM_COMPLETIONS_BY_DAY } from '~/gql/scorm/scorm'
import { client } from '~/helpers/apollo'
import { tourAnchor } from '~/helpers/tour/anchors'
import { VIZ } from '~/pages/backoffice/admin/dashboard/viz'

/** Profondeurs proposées, en jours. Le service plafonne à 180. */
const RANGES = [30, 90, 180] as const

/** Repère du tracé, en unités du viewBox. Le SVG est étiré, pas la donnée. */
const VIEW = { width: 720, height: 180, padTop: 12, padBottom: 24 }

type Point = { day: string, completed: number }

/**
 * Courbe des complétions par jour (#12).
 *
 * Forme choisie pour ce que la donnée doit faire dire : une TENDANCE sur une
 * seule série. D'où une ligne — et son aire en lavis à 10 % pour donner du
 * volume sans peser — plutôt que des barres, qui inviteraient à comparer des
 * jours deux à deux alors que la question est « est-ce que ça monte ? ».
 *
 * Pas de boîte de légende : une série unique est déjà nommée par le titre, et
 * un cartouche à une seule pastille ne ferait que répéter ce titre.
 */
function CompletionTrend() {
  const { t, i18n } = useTranslation()
  const [days, setDays] = useState<number>(30)
  const [hovered, setHovered] = useState<number | null>(null)
  const gradientId = useId()

  const { data, previousData } = useQuery(GQL_SCORM_COMPLETIONS_BY_DAY, {
    client,
    fetchPolicy: 'cache-and-network',
    variables: { input: { days } },
  })

  // On garde la courbe précédente pendant le chargement : la faire disparaître
  // à chaque changement de période ferait clignoter un graphique vide, qui se
  // lit comme « aucune complétion » plutôt que comme « en cours de calcul ».
  const points = (data?.getScormCompletionsByDay
    ?? previousData?.getScormCompletionsByDay
    ?? []) as unknown as Point[]

  const total = points.reduce((sum, p) => sum + p.completed, 0)
  // Plancher à 1 : sans lui, une période sans aucune complétion diviserait par
  // zéro et la courbe partirait à l'infini au lieu de rester à plat.
  const max = Math.max(1, ...points.map(p => p.completed))

  const plotHeight = VIEW.height - VIEW.padTop - VIEW.padBottom
  const x = (index: number) =>
    points.length <= 1 ? 0 : (index / (points.length - 1)) * VIEW.width
  const y = (value: number) => VIEW.padTop + plotHeight - (value / max) * plotHeight

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.completed)}`).join(' ')
  const area = points.length > 0
    ? `${line} L${x(points.length - 1)},${VIEW.padTop + plotHeight} L0,${VIEW.padTop + plotHeight} Z`
    : ''

  const dayLabel = (day: string) =>
    new Date(`${day}T12:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })

  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-sm" {...tourAnchor('dashboard.trend')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-muted-foreground">
            {t('stats.trend.title')}
          </h2>
          {/* Chiffres proportionnels : `tabular-nums` donne à chaque chiffre la
              largeur d'un zéro, ce qui fait bâiller un total à cette taille. */}
          <p className="mt-1 text-2xl font-semibold">{total}</p>
        </div>

        <div {...tourAnchor('dashboard.range')}>
          <SegmentedControl
            label={t('stats.trend.range')}
            options={RANGES.map(range => ({ value: range, label: t('stats.trend.days', { count: range }) }))}
            value={days}
            onChange={setDays}
          />
        </div>
      </div>

      {total === 0
        ? <p className="mt-6 text-sm text-muted-foreground">{t('stats.trend.empty')}</p>
        : (
            <div className="relative mt-4">
              {/*
                Conteneur dédié au tracé : le point de survol se positionne en %
                de CETTE boîte. Ancré au conteneur extérieur, qui porte aussi les
                libellés de dates, il aurait été décalé vers le bas de la hauteur
                de ces libellés — visiblement à côté de la courbe.
              */}
              <div className="relative">
                <svg
                  viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
                  preserveAspectRatio="none"
                  className="h-44 w-full overflow-visible"
                  role="img"
                  aria-label={t('stats.trend.alt', { count: total, days })}
                >
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={VIZ.good} stopOpacity="0.18" />
                      <stop offset="100%" stopColor={VIZ.good} stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Ligne de base, filet plein et non pointillé : le pointillé
                    ajoute du bruit et se lit comme une donnée. */}
                  <line
                    x1="0"
                    y1={VIEW.padTop + plotHeight}
                    x2={VIEW.width}
                    y2={VIEW.padTop + plotHeight}
                    stroke="currentColor"
                    className="text-border"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />

                  <path d={area} fill={`url(#${gradientId})`} />
                  {/*
                  `non-scaling-stroke` : le viewBox est étiré à la largeur du
                  conteneur, donc sans lui le trait s'épaissirait avec l'écran.
                */}
                  <path
                    d={line}
                    fill="none"
                    stroke={VIZ.good}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />

                  {hovered != null && points[hovered] && (
                    <>
                      <line
                        x1={x(hovered)}
                        y1={VIEW.padTop}
                        x2={x(hovered)}
                        y2={VIEW.padTop + plotHeight}
                        stroke="currentColor"
                        className="text-muted-foreground"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                      />
                    </>
                  )}

                  {/*
                  Zones de survol : une bande par jour, plus large que la marque
                  elle-même. Viser un point de 5 px à la souris est intenable —
                  la cible doit être la colonne, pas le point.
                */}
                  {points.map((p, i) => (
                    <rect
                      key={p.day}
                      x={i === 0 ? 0 : x(i) - VIEW.width / (points.length - 1) / 2}
                      y={0}
                      width={VIEW.width / Math.max(1, points.length - 1)}
                      height={VIEW.height}
                      fill="transparent"
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                    />
                  ))}
                </svg>

                {/* Extrémités seulement : une date sous chaque point serait
                  illisible à 180 jours, et personne ne les lirait. */}
                <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                  <span>{points[0] && dayLabel(points[0].day)}</span>
                  <span>{points.at(-1) && dayLabel(points.at(-1)!.day)}</span>
                </div>

                {/*
                Le point de survol est un élément HTML positionné en %, et non un
                `<circle>` SVG : le viewBox est étiré horizontalement
                (`preserveAspectRatio="none"`), ce qui aurait rendu le cercle en
                ellipse. `non-scaling-stroke` protège l'épaisseur des traits, pas
                la géométrie d'une forme. L'anneau reprend la couleur de la carte
                pour rester lisible là où il croise la ligne.
              */}
                {hovered != null && points[hovered] && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card"
                    style={{
                      backgroundColor: VIZ.good,
                      left: `${(x(hovered) / VIEW.width) * 100}%`,
                      top: `${(y(points[hovered].completed) / VIEW.height) * 100}%`,
                    }}
                  />
                )}
              </div>

              {hovered != null && points[hovered] && (
                <div className="pointer-events-none absolute -top-2 left-0 right-0 flex justify-center">
                  <span className="rounded-md border border-border bg-popover px-2 py-1 text-xs shadow-sm">
                    {t('stats.trend.tooltip', {
                      count: points[hovered].completed,
                      day: dayLabel(points[hovered].day),
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
    </section>
  )
}

export default CompletionTrend
